import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowRightLeft, Building2, Calendar, Plus, Truck, MapPin } from 'lucide-react';
import { hasPermission } from '../lib/permissions';
import { appTodayDate, buildAppDateTimeIso } from '../lib/appDateTime';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function TransfersTab({ animalId, animal, user, bases = [], onTransferComplete }) {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewTransferForm, setShowNewTransferForm] = useState(false);
  
  // Form state
  const [toBaseId, setToBaseId] = useState('');
  const [transferDate, setTransferDate] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (animalId) {
      fetchTransfers();
    }
  }, [animalId]);

  const fetchTransfers = async () => {
    try {
      const response = await axios.get(`${API}/api/animals/${animalId}/transfers`);
      setTransfers(response.data);
    } catch (error) {
      console.error('Error fetching transfers:', error);
    }
  };

  const handleCreateTransfer = async (e) => {
    e.preventDefault();
    
    if (!toBaseId) {
      toast.error('Selecione a base de destino');
      return;
    }
    
    if (toBaseId === animal?.base_id) {
      toast.error('A base de destino deve ser diferente da base atual');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/api/transfers`, {
        animal_id: animalId,
        to_base_id: toBaseId,
        transfer_date: transferDate ? buildAppDateTimeIso(transferDate, '00:00') : buildAppDateTimeIso(appTodayDate(), '00:00'),
        reason: reason || null,
        notes: notes || null
      });
      
      toast.success('Transferência registrada com sucesso!');
      setShowNewTransferForm(false);
      setToBaseId('');
      setTransferDate('');
      setReason('');
      setNotes('');
      fetchTransfers();
      
      // Notify parent to refresh animal data
      if (onTransferComplete) {
        onTransferComplete();
      }
    } catch (error) {
      console.error('Error creating transfer:', error);
      toast.error(error.response?.data?.detail || 'Erro ao registrar transferência');
    } finally {
      setLoading(false);
    }
  };

  const canCreateTransfer = hasPermission(user, 'animals.transfer');
  
  // Filter out current base from destinations
  const availableBases = bases.filter(b => b.id !== animal?.base_id);

  // Get base name by ID
  const getBaseName = (baseId) => {
    const base = bases.find(b => b.id === baseId);
    return base?.name || baseId;
  };

  return (
    <div className="space-y-6" data-testid="transfers-tab">
      {/* Current Base Card */}
      <Card className="border-2 border-blue-200 bg-blue-50/50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-blue-800">
            <Building2 className="w-5 h-5" />
            Base Atual
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <MapPin className="w-8 h-8 text-blue-600" />
                <div>
                  <p className="text-xl font-bold text-blue-800">
                    {animal?.base_name || 'Não definido'}
                  </p>
                  <p className="text-sm text-blue-700">CRAS atual: {animal?.ibama_id || 'N/A'}</p>
                  {animal?.pmp_id && <p className="text-sm text-sky-700">PMP: {animal.pmp_id}</p>}
                  <p className="text-sm text-blue-600">
                    {animal?.base_city && `${animal.base_city}, ${animal.base_state || ''}`}
                  </p>
                </div>
              </div>
            </div>
            {canCreateTransfer && availableBases.length > 0 && (
              <Button
                onClick={() => setShowNewTransferForm(!showNewTransferForm)}
                className="bg-blue-600 hover:bg-blue-700 min-h-[44px]"
                data-testid="new-transfer-btn"
              >
                <ArrowRightLeft className="w-4 h-4 mr-2" />
                {showNewTransferForm ? 'Cancelar' : 'Transferir'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* New Transfer Form */}
      {showNewTransferForm && (
        <Card className="border-blue-200" data-testid="new-transfer-form">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-800">
              <Truck className="w-5 h-5" />
              Nova Transferência
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateTransfer} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* From (current base - readonly) */}
                <div className="p-4 bg-gray-100 rounded-lg">
                  <Label className="text-gray-600">De (Origem)</Label>
                  <p className="font-semibold text-lg mt-1">
                    {animal?.base_name}
                  </p>
                </div>

                {/* To (destination) */}
                <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                  <Label className="text-blue-700 font-semibold">Para (Destino) *</Label>
                  <Select value={toBaseId} onValueChange={setToBaseId}>
                    <SelectTrigger className="mt-2" data-testid="to-base-select">
                      <SelectValue placeholder="Selecione a base" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableBases.map(base => (
                        <SelectItem key={base.id} value={base.id}>
                          {base.name} - {base.city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Data da Transferência</Label>
                  <Input
                    type="datetime-local"
                    value={transferDate}
                    onChange={(e) => setTransferDate(e.target.value)}
                    data-testid="transfer-date-input"
                  />
                </div>
                <div>
                  <Label>Motivo</Label>
                  <Select value={reason} onValueChange={setReason}>
                    <SelectTrigger data-testid="transfer-reason-select">
                      <SelectValue placeholder="Selecione o motivo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="capacity">Capacidade da base</SelectItem>
                      <SelectItem value="treatment">Tratamento especializado</SelectItem>
                      <SelectItem value="release">Preparação para soltura</SelectItem>
                      <SelectItem value="other">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Observações</Label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Observações sobre a transferência (opcional)"
                  data-testid="transfer-notes-input"
                />
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowNewTransferForm(false)}
                  className="min-h-[44px]"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 min-h-[44px]"
                  data-testid="submit-transfer-btn"
                >
                  {loading ? 'Transferindo...' : 'Confirmar Transferência'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Transfer History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-800">
            <Calendar className="w-5 h-5" />
            Histórico de Transferências
          </CardTitle>
        </CardHeader>
        <CardContent>
          {transfers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <ArrowRightLeft className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p>Nenhuma transferência registrada</p>
              <p className="text-sm">O animal permanece na base original desde a admissão</p>
            </div>
          ) : (
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-blue-200 hidden sm:block" />
              
              <div className="space-y-4">
                {transfers.map((transfer, index) => (
                  <div 
                    key={transfer.id} 
                    className="relative flex items-start gap-4"
                    data-testid={`transfer-item-${index}`}
                  >
                    {/* Timeline dot */}
                    <div className="hidden sm:flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 border-2 border-blue-500 z-10 flex-shrink-0">
                      <Truck className="w-4 h-4 text-blue-600" />
                    </div>
                    
                    {/* Transfer card */}
                    <Card className="flex-1 border-l-4 border-l-blue-500">
                      <CardContent className="pt-4">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-2">
                          {/* From -> To */}
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="text-gray-600">
                              {getBaseName(transfer.from_base_id)}
                            </Badge>
                            <ArrowRightLeft className="w-4 h-4 text-blue-600" />
                            <Badge className="bg-blue-100 text-blue-800 border-blue-300">
                              {getBaseName(transfer.to_base_id)}
                            </Badge>
                          </div>
                        </div>
                        
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm text-gray-600">
                          <span>
                            {new Date(transfer.transferred_at).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                          {transfer.reason && (
                            <>
                              <span className="hidden sm:inline">•</span>
                              <Badge variant="outline" className="text-xs">
                                {transfer.reason === 'capacity' && 'Capacidade'}
                                {transfer.reason === 'treatment' && 'Tratamento'}
                                {transfer.reason === 'release' && 'Soltura'}
                                {transfer.reason === 'other' && 'Outro'}
                              </Badge>
                            </>
                          )}
                          {transfer.notes && (
                            <>
                              <span className="hidden sm:inline">•</span>
                              <span className="italic">{transfer.notes}</span>
                            </>
                          )}
                        </div>
                        {(transfer.previous_ibama_id || transfer.new_ibama_id) && (
                          <div className="mt-2 text-sm text-blue-700">
                            CRAS: {transfer.previous_ibama_id || 'N/A'} → {transfer.new_ibama_id || 'N/A'}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
